import { Link } from "react-router-dom";
import {
    ArrowRight,
    Sparkles,
    Search,
    Handshake,
    CreditCard,
    Package,
    RotateCcw,
    Star,
    PlusCircle,
    Bell,
    ShieldCheck,
    Wallet,
    BadgeCheck,
    Lock,
} from "lucide-react";

/**
 * Standalone "How it works" page. Deep-dive walkthrough of both sides of
 * the marketplace — Borrowing (renter) and Lending (owner) — presented as
 * two side-by-side numbered flows, with a trust section and a small FAQ.
 * Linked from Footer and the Landing hero.
 */
const BORROW_STEPS = [
    {
        icon: Search,
        title: "Browse items nearby",
        body: "Search by keyword or category — drills, tents, mixers, ladders. Every item belongs to a neighbor on your block, not a warehouse.",
    },
    {
        icon: Handshake,
        title: "Request the dates you need",
        body: "Pick a date range on the item card and send a request. Your neighbor gets notified by email + in-app + push (if enabled).",
    },
    {
        icon: CreditCard,
        title: "Pay when they accept",
        body: "Once the lender accepts, the rental moves to Pending Payment in your Borrowing dashboard. Pay securely with card, Apple Pay, Google Pay, Cash App Pay or Link.",
    },
    {
        icon: Package,
        title: "Pick it up",
        body: "The exact pickup address unlocks the moment you pay. Directions and instructions go straight to your email + Borrowing card.",
    },
    {
        icon: RotateCcw,
        title: "Return when the rental ends",
        body: "On the last day of your rental, we'll email a reminder. Tap Return item in Borrowing, hand it back, and the lender confirms.",
    },
    {
        icon: Star,
        title: "Leave a review",
        body: "Once the return is confirmed the rental closes out. A one-tap review helps future neighbors decide who's reliable.",
    },
];

const LEND_STEPS = [
    {
        icon: PlusCircle,
        title: "List your item",
        body: "Tap List Item, add a name, category, and daily price. Our AI suggests a fair price so you don't have to guess.",
    },
    {
        icon: Bell,
        title: "Get notified on requests",
        body: "When a neighbor requests your item, you're pinged by email + in-app + push (if enabled). Review their profile and dates, then accept or decline in one tap.",
    },
    {
        icon: CreditCard,
        title: "Renter pays through Stripe",
        body: "As soon as they pay, 90% is credited to your Lendly balance (10% platform fee). You can see running earnings on the Lending dashboard.",
    },
    {
        icon: Handshake,
        title: "Hand it off",
        body: "Coordinate pickup at your listed address (only visible after payment). Add pickup instructions to keep it smooth — gate codes, doorbell, etc.",
    },
    {
        icon: RotateCcw,
        title: "Confirm the return",
        body: "When the renter marks the item returned, you get a notification. Give it a quick check and tap Accept return. Rental closes and payout unlocks.",
    },
    {
        icon: Wallet,
        title: "Withdraw your earnings",
        body: "Earnings accrue as Lendly credits automatically. Connect Stripe payouts once, tap Withdraw whenever you want, and money lands in your bank in 5–10 business days.",
    },
];

const TRUST_POINTS = [
    {
        icon: ShieldCheck,
        title: "Payments protected by Stripe",
        body: "Every transaction runs on Stripe — the same processor behind Amazon, Shopify and Instacart. Your card details never touch Lendly's servers.",
    },
    {
        icon: BadgeCheck,
        title: "Verified neighbors only",
        body: "Lendly is invite-only by neighborhood. Every member confirms their address before they can list or borrow. Ratings keep the community honest.",
    },
    {
        icon: Lock,
        title: "Your address stays yours",
        body: "Exact addresses are only shared with a renter after payment is confirmed. Browse pages show street names and neighborhoods — never your front door.",
    },
];

const FAQ = [
    {
        q: "What if my neighbor damages my item?",
        a: "Lenders can set a refundable deposit at listing time. If something's off at return, you can withhold part of the deposit and message the renter. Disputes route to Lendly review.",
    },
    {
        q: "Do I need to set up Stripe before I list?",
        a: "No. Start listing right away — your earnings accrue as Lendly credits automatically. You only need to connect Stripe when you want to withdraw those credits to your bank.",
    },
    {
        q: "What's the fee?",
        a: "Lendly takes a flat 10% platform fee from each rental. Renters see the total upfront — no hidden charges, no surprise fees at checkout.",
    },
    {
        q: "Can I cancel a rental?",
        a: "Yes. Renters can cancel before payment. Lenders can decline requests any time before accepting. Once paid, cancellations are handled case-by-case — reach out to Lendly.",
    },
];

const StepCard = ({ step, index, tone = "olive" }) => {
    const Icon = step.icon;
    const isOlive = tone === "olive";
    return (
        <div
            className="relative rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-6 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(46,50,32,0.08)] animate-fade-up"
            style={{ animationDelay: `${index * 60}ms` }}
        >
            <span className="absolute right-5 top-5 font-display text-3xl font-bold text-[#2e3220]/10">
                {String(index + 1).padStart(2, "0")}
            </span>
            <div
                className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl ${
                    isOlive ? "bg-[#737a3d] text-[#F4F1EA]" : "bg-[#2e3220] text-[#F4F1EA]"
                }`}
            >
                <Icon className="h-5 w-5" strokeWidth={2.25} />
            </div>
            <h3 className="font-display text-lg font-bold text-[#2e3220]">{step.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-[#5a6142]">{step.body}</p>
        </div>
    );
};

const HowItWorks = () => {
    return (
        <main data-testid="how-it-works-page" className="relative z-10">
            {/* Hero */}
            <section className="px-6 pt-16 pb-14 md:px-10 md:pt-24">
                <div className="mx-auto max-w-5xl text-center">
                    <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA]/70 px-3 py-1.5 backdrop-blur">
                        <Sparkles className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                        <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                            How Lendly works
                        </span>
                    </div>
                    <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-[#2e3220] sm:text-5xl lg:text-6xl">
                        Borrow from your block.{" "}
                        <span className="relative inline-block">
                            <span className="relative z-10 text-[#737a3d]">Lend from your garage.</span>
                            <span
                                aria-hidden="true"
                                className="absolute inset-x-0 bottom-1 h-3 rounded-full bg-[#8a9450]/30"
                            />
                        </span>
                    </h1>
                    <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-[#5a6142] sm:text-lg">
                        Lendly is built around two simple flows — one for the neighbor who needs
                        something, one for the neighbor who has it. Here's exactly how each
                        side works, from first click to final review.
                    </p>
                </div>
            </section>

            {/* Two-column flows */}
            <section className="px-6 pb-20 md:px-10">
                <div className="mx-auto max-w-7xl">
                    <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
                        {/* Borrowing column */}
                        <div>
                            <div className="mb-6 rounded-3xl border border-[#737a3d]/25 bg-[#737a3d]/8 p-6">
                                <div className="inline-flex items-center gap-2 rounded-full bg-[#737a3d] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#F4F1EA]">
                                    <Package className="h-3 w-3" strokeWidth={2.5} />
                                    For neighbors borrowing
                                </div>
                                <h2 className="mt-3 font-display text-2xl font-bold text-[#2e3220] sm:text-3xl">
                                    You need it for a day, not for life.
                                </h2>
                                <p className="mt-2 text-sm leading-relaxed text-[#5a6142]">
                                    Six steps from finding a drill on Browse to leaving a review — pay
                                    a fair daily rate, skip the store trip, meet a neighbor.
                                </p>
                            </div>
                            <div className="space-y-4">
                                {BORROW_STEPS.map((s, i) => (
                                    <StepCard key={s.title} step={s} index={i} tone="olive" />
                                ))}
                            </div>
                        </div>

                        {/* Lending column */}
                        <div>
                            <div className="mb-6 rounded-3xl border border-[#2e3220]/15 bg-[#2e3220]/5 p-6">
                                <div className="inline-flex items-center gap-2 rounded-full bg-[#2e3220] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#F4F1EA]">
                                    <Wallet className="h-3 w-3" strokeWidth={2.5} />
                                    For neighbors lending
                                </div>
                                <h2 className="mt-3 font-display text-2xl font-bold text-[#2e3220] sm:text-3xl">
                                    Your garage is a business waiting to happen.
                                </h2>
                                <p className="mt-2 text-sm leading-relaxed text-[#5a6142]">
                                    Six steps from a photo of your power washer to money in your bank.
                                    Lendly credits accrue automatically — connect Stripe when you're
                                    ready to withdraw.
                                </p>
                            </div>
                            <div className="space-y-4">
                                {LEND_STEPS.map((s, i) => (
                                    <StepCard key={s.title} step={s} index={i} tone="ink" />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trust */}
            <section className="bg-[#E8E2D0]/40 px-6 py-20 md:px-10">
                <div className="mx-auto max-w-7xl">
                    <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                                Built on trust
                            </p>
                            <h2 className="mt-2 font-display text-3xl font-bold text-[#2e3220] sm:text-4xl">
                                Safe payments, verified neighbors, real privacy.
                            </h2>
                        </div>
                        <p className="max-w-md text-sm leading-relaxed text-[#5a6142]">
                            Lending between neighbors only works when everyone feels safe. Here's
                            what keeps the marketplace honest.
                        </p>
                    </div>
                    <div className="grid gap-6 md:grid-cols-3">
                        {TRUST_POINTS.map((t, i) => {
                            const Icon = t.icon;
                            return (
                                <div
                                    key={t.title}
                                    className="rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-6 animate-fade-up"
                                    style={{ animationDelay: `${i * 90}ms` }}
                                >
                                    <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#737a3d]/15 text-[#737a3d]">
                                        <Icon className="h-5 w-5" strokeWidth={2.25} />
                                    </div>
                                    <h3 className="font-display text-lg font-bold text-[#2e3220]">
                                        {t.title}
                                    </h3>
                                    <p className="mt-1.5 text-sm leading-relaxed text-[#5a6142]">
                                        {t.body}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="px-6 py-20 md:px-10">
                <div className="mx-auto max-w-4xl">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                        Common questions
                    </p>
                    <h2 className="mt-2 font-display text-3xl font-bold text-[#2e3220] sm:text-4xl">
                        The small print, in plain words.
                    </h2>
                    <div className="mt-8 space-y-3">
                        {FAQ.map((f, i) => (
                            <details
                                key={f.q}
                                data-testid={`how-faq-${i + 1}`}
                                className="group rounded-2xl border border-[#2e3220]/10 bg-[#F4F1EA] p-5"
                            >
                                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-base font-bold text-[#2e3220]">
                                    {f.q}
                                    <span className="text-lg font-bold text-[#737a3d] transition-transform duration-200 group-open:rotate-45">
                                        +
                                    </span>
                                </summary>
                                <p className="mt-3 text-sm leading-relaxed text-[#5a6142]">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 pb-24 md:px-10">
                <div className="mx-auto max-w-5xl rounded-[40px] bg-[#2e3220] px-8 py-14 text-center text-[#F4F1EA] md:px-16 md:py-20">
                    <h2 className="font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
                        Ready to try it?
                    </h2>
                    <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-[#F4F1EA]/75">
                        Browse what's on your block, or list your first item in under a minute.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                        <Link
                            to="/browse"
                            data-testid="how-cta-browse"
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F4F1EA] px-7 py-3 text-base font-bold text-[#2e3220] transition-all duration-200 hover:-translate-y-0.5 hover:bg-white"
                        >
                            Browse items
                            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                        </Link>
                        <Link
                            to="/list"
                            data-testid="how-cta-list"
                            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#F4F1EA] px-7 py-[10px] text-base font-bold text-[#F4F1EA] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#F4F1EA]/10"
                        >
                            List your first item
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default HowItWorks;
